/**
 * @fileoverview GoogleMeetModal component for creating Google Meet calls with clients.
 * Allows selecting employees and scheduling meeting times before sending invites.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faTimes, faUserMinus, faCalendar, faClock, faVideo } from '@fortawesome/free-solid-svg-icons';
import './styles/GoogleMeetModal.css';

/**
 * GoogleMeetModal component for creating Google Meet invitations
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Object} props.clientData - Client data including client_id and name
 * @returns {React.ReactElement} The rendered GoogleMeetModal component
 */
const GoogleMeetModal = ({ isOpen, onClose, clientData }) => {
  const { getAccessToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingMeetings, setLoadingMeetings] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [existingMeetings, setExistingMeetings] = useState([]);
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingDescription, setMeetingDescription] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [duration, setDuration] = useState(30);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  /**
   * Fetch assigned employees when modal opens
   */
  useEffect(() => {
    if (isOpen && clientData?.client_id) {
      fetchAssignedEmployees();
      fetchExistingMeetings();
      
      // Set default values
      const clientName = clientData.First_Legal_Name && clientData.Last_Legal_Name
        ? `${clientData.First_Legal_Name} ${clientData.Last_Legal_Name}`
        : clientData.name || 'Client';
      
      setMeetingTitle(`Meeting with ${clientName}`);
      setMeetingDescription(`Scheduled meeting with ${clientName} and the Snapped team.`);
      
      // Set default to tomorrow at 2pm
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(14, 0, 0, 0);
      
      setMeetingDate(tomorrow.toISOString().split('T')[0]);
      setMeetingTime('14:00');
    }
  }, [isOpen, clientData]);

  /**
   * Fetch employees assigned to the client
   */
  const fetchAssignedEmployees = async () => {
    setLoadingEmployees(true);
    setError('');
    
    try {
      const token = await getAccessToken();
      const response = await axios.get(
        API_ENDPOINTS.CALENDAR.ASSIGNED_EMPLOYEES(clientData.client_id),
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data && response.data.employees) {
        setEmployees(response.data.employees);
        // Select all employees by default
        setSelectedEmployees(response.data.employees.map(emp => emp.user_id));
      }
    } catch (err) {
      console.error('Error fetching assigned employees:', err);
      setError('Failed to load assigned employees. Please try again.');
    } finally {
      setLoadingEmployees(false);
    }
  };

  /**
   * Fetch existing meetings with this client
   */
  const fetchExistingMeetings = async () => {
    setLoadingMeetings(true);
    
    try {
      const token = await getAccessToken();
      const response = await axios.get(
        API_ENDPOINTS.CALENDAR.GET_MEETINGS(clientData.client_id),
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data && response.data.meetings) {
        setExistingMeetings(response.data.meetings);
      }
    } catch (err) {
      console.error('Error fetching existing meetings:', err);
      // Don't show error for meetings fetch - not critical
    } finally {
      setLoadingMeetings(false);
    }
  };

  /**
   * Cancel an existing meeting
   */
  const cancelMeeting = async (eventId) => {
    if (!window.confirm('Are you sure you want to cancel this meeting? All attendees will be notified.')) {
      return;
    }

    try {
      const token = await getAccessToken();
      await axios.delete(
        API_ENDPOINTS.CALENDAR.CANCEL_MEETING(eventId),
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Remove from local state
      setExistingMeetings(prev => prev.filter(meeting => meeting.id !== eventId));
      setSuccess('Meeting cancelled successfully!');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error cancelling meeting:', err);
      setError('Failed to cancel meeting. Please try again.');
    }
  };

  /**
   * Format date and time for display
   */
  const formatMeetingDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  /**
   * Toggle employee selection
   */
  const toggleEmployee = (userId) => {
    setSelectedEmployees(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  /**
   * Remove employee from selection
   */
  const removeEmployee = (userId) => {
    setSelectedEmployees(prev => prev.filter(id => id !== userId));
  };

  /**
   * Handle form submission to create Google Meet
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate inputs
      if (!meetingDate || !meetingTime) {
        setError('Please select a date and time for the meeting.');
        setLoading(false);
        return;
      }

      if (selectedEmployees.length === 0) {
        setError('Please select at least one employee to invite.');
        setLoading(false);
        return;
      }

      // Combine date and time into ISO format
      const meetingDateTime = new Date(`${meetingDate}T${meetingTime}`);
      const startTime = meetingDateTime.toISOString();

      const token = await getAccessToken();
      const response = await axios.post(
        API_ENDPOINTS.CALENDAR.CREATE_MEET(clientData.client_id),
        {
          employee_ids: selectedEmployees,
          start_time: startTime,
          duration_minutes: duration,
          title: meetingTitle,
          description: meetingDescription
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data && response.data.success) {
        setSuccess(
          `Google Meet created successfully! Invitations sent to client and ${selectedEmployees.length} employee(s).`
        );
        
        // Refresh the meetings list
        fetchExistingMeetings();
        
        // Close modal after 2 seconds
        setTimeout(() => {
          onClose();
          resetForm();
        }, 2000);
      }
    } catch (err) {
      console.error('Error creating Google Meet:', err);
      setError(
        err.response?.data?.detail || 
        'Failed to create Google Meet. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset form to default state
   */
  const resetForm = () => {
    setSelectedEmployees([]);
    setExistingMeetings([]);
    setMeetingTitle('');
    setMeetingDescription('');
    setMeetingDate('');
    setMeetingTime('');
    setDuration(30);
    setError('');
    setSuccess('');
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const clientName = clientData?.First_Legal_Name && clientData?.Last_Legal_Name
    ? `${clientData.First_Legal_Name} ${clientData.Last_Legal_Name}`
    : clientData?.name || 'Client';

  return (
    <div className="googlemeet-modal-overlay" onClick={handleClose}>
      <div className="googlemeet-modal" onClick={(e) => e.stopPropagation()}>
        <div className="googlemeet-modal-header">
          <h2>
            <FontAwesomeIcon icon={faPhone} /> Schedule Google Meet
          </h2>
          <button className="googlemeet-close-button" onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="googlemeet-modal-body">
          {/* Existing Meetings Section */}
          {loadingMeetings ? (
            <div className="googlemeet-existing-meetings-section">
              <p className="googlemeet-loading-text">Loading scheduled meetings...</p>
            </div>
          ) : existingMeetings.length > 0 ? (
            <div className="googlemeet-existing-meetings-section">
              <h4>
                <FontAwesomeIcon icon={faVideo} /> Upcoming Meetings
              </h4>
              <div className="googlemeet-existing-meetings-list">
                {existingMeetings.map((meeting) => (
                  <div key={meeting.id} className="googlemeet-existing-meeting-item">
                    <div className="googlemeet-meeting-info">
                      <div className="googlemeet-meeting-title">{meeting.summary}</div>
                      <div className="googlemeet-meeting-time">
                        <FontAwesomeIcon icon={faCalendar} /> {formatMeetingDateTime(meeting.start_time)}
                      </div>
                      {meeting.meet_link && (
                        <a 
                          href={meeting.meet_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="googlemeet-meeting-link"
                        >
                          <FontAwesomeIcon icon={faVideo} /> Join Meeting
                        </a>
                      )}
                    </div>
                    <button
                      type="button"
                      className="googlemeet-cancel-meeting-btn"
                      onClick={() => cancelMeeting(meeting.id)}
                      title="Cancel this meeting"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="googlemeet-client-info">
            <h3>Client: {clientName}</h3>
            {clientData?.Email_Address && (
              <p className="googlemeet-client-email">{clientData.Email_Address}</p>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            {/* Meeting Details */}
            <div className="googlemeet-form-section">
              <h4>Meeting Details</h4>
              
              <div className="googlemeet-form-group">
                <label htmlFor="meeting-title">Meeting Title</label>
                <input
                  id="meeting-title"
                  type="text"
                  value={meetingTitle}
                  onChange={(e) => setMeetingTitle(e.target.value)}
                  placeholder="Enter meeting title"
                  required
                />
              </div>

              <div className="googlemeet-form-group">
                <label htmlFor="meeting-description">Description (Optional)</label>
                <textarea
                  id="meeting-description"
                  value={meetingDescription}
                  onChange={(e) => setMeetingDescription(e.target.value)}
                  placeholder="Enter meeting description"
                  rows="3"
                />
              </div>

              <div className="googlemeet-form-row">
                <div className="googlemeet-form-group">
                  <label htmlFor="meeting-date">
                    <FontAwesomeIcon icon={faCalendar} /> Date
                  </label>
                  <input
                    id="meeting-date"
                    type="date"
                    value={meetingDate}
                    onChange={(e) => setMeetingDate(e.target.value)}
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="googlemeet-form-group">
                  <label htmlFor="meeting-time">
                    <FontAwesomeIcon icon={faClock} /> Time
                  </label>
                  <input
                    id="meeting-time"
                    type="time"
                    value={meetingTime}
                    onChange={(e) => setMeetingTime(e.target.value)}
                    required
                  />
                </div>

                <div className="googlemeet-form-group">
                  <label htmlFor="duration">Duration (min)</label>
                  <select
                    id="duration"
                    value={duration}
                    onChange={(e) => setDuration(Number(e.target.value))}
                  >
                    <option value="15">15 min</option>
                    <option value="30">30 min</option>
                    <option value="45">45 min</option>
                    <option value="60">1 hour</option>
                    <option value="90">1.5 hours</option>
                    <option value="120">2 hours</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Employee Selection */}
            <div className="googlemeet-form-section">
              <h4>Invite Employees</h4>
              {loadingEmployees ? (
                <p className="googlemeet-loading-text">Loading employees...</p>
              ) : employees.length === 0 ? (
                <p className="googlemeet-no-employees-text">No employees assigned to this client.</p>
              ) : (
                <>
                  <p className="googlemeet-helper-text">
                    Select employees to invite. Click to toggle selection or use the × button to remove.
                  </p>
                  <div className="googlemeet-employee-list">
                    {employees.map((employee) => {
                      const isSelected = selectedEmployees.includes(employee.user_id);
                      return (
                        <div
                          key={employee.user_id}
                          className={`googlemeet-employee-card ${isSelected ? 'selected' : ''}`}
                        >
                          <div
                            className="googlemeet-employee-card-content"
                            onClick={() => toggleEmployee(employee.user_id)}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleEmployee(employee.user_id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="googlemeet-employee-info">
                              <span className="googlemeet-employee-name">{employee.full_name}</span>
                              {employee.work_email && (
                                <span className="googlemeet-employee-email">{employee.work_email}</span>
                              )}
                            </div>
                          </div>
                          {isSelected && (
                            <button
                              type="button"
                              className="googlemeet-remove-employee-btn"
                              onClick={() => removeEmployee(employee.user_id)}
                              title="Remove from invitation"
                            >
                              <FontAwesomeIcon icon={faUserMinus} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="googlemeet-selection-summary">
                    Selected: {selectedEmployees.length} of {employees.length} employees
                  </div>
                </>
              )}
            </div>

            {/* Error/Success Messages */}
            {error && <div className="googlemeet-error-message">{error}</div>}
            {success && <div className="googlemeet-success-message">{success}</div>}

            {/* Actions */}
            <div className="googlemeet-modal-actions">
              <button
                type="button"
                className="googlemeet-cancel-button"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="googlemeet-submit-button"
                disabled={loading || loadingEmployees || selectedEmployees.length === 0}
              >
                {loading ? (
                  <>
                    <span className="googlemeet-spinner"></span> Creating Meet...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPhone} /> Create & Send Invites
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GoogleMeetModal;

